// MON AGENDA (migration 053) : l'adresse iCal secrète de chacun, et les
// créneaux libres qu'on en déduit pour inviter en masse.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';

export interface MyCalendar {
  configured: boolean;
  /** « Google Agenda », « Outlook », « iCloud » — jamais l'adresse. */
  host: string | null;
  /** L'e-mail de l'agenda (056) : destinataire des avis de réservation. */
  email: string | null;
  /** L'e-mail du compte, utilisé à défaut. */
  accountEmail: string | null;
  checkedAt: string | null;
  error: string | null;
  demo: boolean;
}

export interface FreeSlots {
  free: string[];
  /** Une proposition répartie : un matin, un après-midi, jour après jour. */
  proposed: string[];
  busy: number;
  timeZone: string;
}

const KEY = ['my-calendar'] as const;

export function useMyCalendar() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => api.get<MyCalendar>('/api/me/calendar'),
    staleTime: 60 * 1000,
  });
}

export function useSaveCalendar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ url, email }: { url: string; email?: string }) =>
      api.put<MyCalendar & { events: number }>('/api/me/calendar', { url, ...(email ? { email } : {}) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Changer seulement l'e-mail de l'agenda. */
export function useSetCalendarEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => api.request<MyCalendar>('/api/me/calendar', { method: 'PATCH', body: { email } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** L'e-mail que porte une adresse iCal de Google (…/calendar/ical/<e-mail>/…), ou ''. */
export function emailFromCalendarUrl(raw: string): string {
  const m = /\/calendar\/ical\/([^/]+)\//.exec(raw);
  if (!m) return '';
  try {
    const email = decodeURIComponent(m[1]!).toLowerCase();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !email.endsWith('calendar.google.com') ? email : '';
  } catch {
    return '';
  }
}

export function useRemoveCalendar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.del<{ configured: boolean }>('/api/me/calendar'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

type FreeQuery = { days: number; startHour: number; endHour: number; durationMin: number; count: number };

const freePath = (q: FreeQuery) => {
  const params = new URLSearchParams({
    ...Object.fromEntries(Object.entries(q).map(([k, v]) => [k, String(v)])),
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  return `/api/me/calendar/free?${params}`;
};

/** Mes créneaux libres, relus quand la période ou la durée change (créneaux à cocher). */
export function useFreeSlotsQuery(q: FreeQuery, enabled: boolean) {
  return useQuery({
    queryKey: ['my-calendar', 'free', q] as const,
    queryFn: () => api.get<FreeSlots>(freePath(q)),
    enabled,
    staleTime: 60 * 1000,
    retry: false,
  });
}

/** Mes créneaux libres : demandés à la volée, au clic. */
export function useFreeSlots() {
  return useMutation({
    mutationFn: (q: { days: number; startHour: number; endHour: number; durationMin: number; count: number }) => {
      const params = new URLSearchParams({
        ...Object.fromEntries(Object.entries(q).map(([k, v]) => [k, String(v)])),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      return api.get<FreeSlots>(`/api/me/calendar/free?${params}`);
    },
  });
}

/**
 * Un fichier .ics (Outlook, Apple Calendar…) pour une séance confirmée, en
 * adresse `data:` à télécharger. Les retours à la ligne et virgules sont
 * échappés comme le veut le format iCal.
 */
export function icsHref({ title, startsAt, durationMin, details = '' }: {
  title: string; startsAt: string; durationMin: number; details?: string;
}): string {
  const fmt = (t: number) => new Date(t).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const esc = (v: string) => v.replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/[,;]/g, (c) => `\\${c}`);
  const start = new Date(startsAt).getTime();
  const ics = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Zygosight//Rendez-vous//FR', 'BEGIN:VEVENT',
    `UID:${start}-${Math.random().toString(36).slice(2)}@zygosight`,
    `DTSTAMP:${fmt(Date.now())}`, `DTSTART:${fmt(start)}`, `DTEND:${fmt(start + durationMin * 60_000)}`,
    `SUMMARY:${esc(title)}`,
    ...(details ? [`DESCRIPTION:${esc(details)}`, `LOCATION:${esc(details)}`] : []),
    'END:VEVENT', 'END:VCALENDAR',
  ].join('\r\n');
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;
}

/** Lien « Ajouter à Google Agenda » d'une séance confirmée. */
export function googleCalendarLink({ title, startsAt, durationMin, details = '' }: {
  title: string; startsAt: string; durationMin: number; details?: string;
}): string {
  const fmt = (t: number) => new Date(t).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const start = new Date(startsAt).getTime();
  const q = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${fmt(start)}/${fmt(start + durationMin * 60_000)}`,
    ...(details ? { details, location: details } : {}),
  });
  return `https://calendar.google.com/calendar/render?${q}`;
}
